import React, { useState, useEffect } from 'react';
import { X, Folder, AlertCircle } from 'lucide-react';
import api from '../../api/client';

export default function ProjectCreateModal({
  isOpen,
  onClose,
  onCreated,
  initialProject = null,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('NOT_STARTED');
  const [priority, setPriority] = useState('MEDIUM');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (initialProject) {
        setName(initialProject.name || '');
        setDescription(initialProject.description || '');
        setStatus(initialProject.status || 'NOT_STARTED');
        setPriority(initialProject.priority || 'MEDIUM');
        setStartAt(initialProject.start_at ? initialProject.start_at.slice(0, 16) : '');
        setEndAt(initialProject.end_at ? initialProject.end_at.slice(0, 16) : '');
        setVisibility(initialProject.visibility || 'PRIVATE');
        setSelectedTeamId(initialProject.team_id || '');
      } else {
        setName('');
        setDescription('');
        setStatus('NOT_STARTED');
        setPriority('MEDIUM');
        setStartAt('');
        setEndAt('');
        setVisibility('PRIVATE');
        setSelectedTeamId('');
      }
      setError('');
      fetchUserTeams();
    }
  }, [isOpen, initialProject]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (isOpen) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [isOpen]);

  const fetchUserTeams = async () => {
    try {
      const res = await api.get('/teams');
      setTeams(res.data || []);
    } catch (err) {
      // ignore
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Project name is required.');
      return;
    }

    if (startAt && endAt) {
      if (new Date(endAt) < new Date(startAt)) {
        setError('End date cannot be before start date.');
        return;
      }
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        status,
        priority,
        start_at: startAt ? new Date(startAt).toISOString() : undefined,
        end_at: endAt ? new Date(endAt).toISOString() : undefined,
        visibility,
        team_id: selectedTeamId || undefined,
      };

      let res;
      if (initialProject) {
        res = await api.put(`/projects/${initialProject.id}`, payload);
      } else {
        res = await api.post('/projects', payload);
      }

      if (onCreated) onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer task-drawer project-create-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={initialProject ? 'Edit Project' : 'Create New Project'}
      >
        <div className="card-drawer-header modal-header project-create-header">
          <div className="project-create-title-group">
            <h2 className="modal-title">
              {initialProject ? 'Edit Project' : 'Create New Project'}
            </h2>
            <p className="modal-subtitle">
              Configure project details, milestones, and workspace visibility.
            </p>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="project-create-form" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="card-drawer-body project-create-body" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto' }}>
            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.12)',
                  color: 'var(--danger)',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                Project Name <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Mobile App Redesign 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
                style={{ width: '100%' }}
              />
            </div>

            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Status
                </label>
                <select
                  className="input-field"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="PLANNING">Planning</option>
                  <option value="NOT_STARTED">Not Started</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Priority
                </label>
                <select
                  className="input-field"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent ⚡</option>
                </select>
              </div>
            </div>

            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  className="input-field"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Target End Date
                </label>
                <input
                  type="datetime-local"
                  className="input-field"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Visibility
                </label>
                <select
                  className="input-field"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="PRIVATE">Private (Invite only)</option>
                  <option value="TEAM">Team Access</option>
                  <option value="PUBLIC">Workspace Public</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Assigned Team (Optional)
                </label>
                <select
                  className="input-field"
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">(No specific team)</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                Description / Goals
              </label>
              <textarea
                className="input-field"
                rows={3}
                placeholder="High-level milestones and project deliverables..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="card-drawer-footer modal-footer project-create-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !name.trim()}>
              <Folder size={15} />
              <span>{saving ? 'Saving...' : initialProject ? 'Save Changes' : 'Create Project'}</span>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
